import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Shield, Check, X, CheckCircle2, XCircle } from "lucide-react";

export default function InvitationResponse() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/invitations/${token}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
  }, [token]);

  const respond = async (response) => {
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/invitations/${token}/respond`, { response, responder_name: name || undefined });
      setDone(res.status);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  if (notFound) {
    return <Shell><div data-testid="invitation-not-found" className="text-center text-white/70">This invitation link is invalid or has expired.</div></Shell>;
  }
  if (!data) return <Shell><div className="text-center text-white/60 label-overline">Loading invitation…</div></Shell>;

  const team = data.team || {};
  const player = data.player || {};
  const finalStatus = done || (["accepted", "declined", "confirmed"].includes(data.status) ? data.status : null);

  if (finalStatus) {
    const accepted = finalStatus === "accepted" || finalStatus === "confirmed";
    return (
      <Shell>
        <div data-testid="invitation-result" className="text-center">
          {accepted ? <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400" /> : <XCircle className="w-16 h-16 mx-auto text-red-400" />}
          <h2 className="font-display text-3xl uppercase tracking-tight mt-4 text-white">{accepted ? "You're in!" : "Response recorded"}</h2>
          <p className="text-white/70 mt-2 text-sm">
            {accepted
              ? `${player.first_name} ${player.last_name} has accepted the invitation to ${team.name}. The CBR staff will be in touch with next steps.`
              : `We've recorded that ${player.first_name} ${player.last_name} declined the invitation to ${team.name}.`}
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="invitation-card">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><Shield className="w-4 h-4" /> CBR Nation Invitation</div>
        <h1 className="font-display text-4xl uppercase tracking-tighter mt-2 text-white">{team.name}</h1>
        <p className="text-white/60 text-sm mt-1">{[team.season, team.division].filter(Boolean).join(" · ")}</p>
        {team.description && <p className="text-white/70 text-sm mt-3">{team.description}</p>}

        <div className="mt-6 bg-white/5 border border-white/10 rounded-sm p-5">
          <div className="label-overline text-white/40 text-[10px]">Invited Player</div>
          <div className="font-display text-2xl uppercase tracking-tight text-white mt-1">{player.first_name} {player.last_name}</div>
          <div className="text-white/60 text-xs mt-1">{[player.primary_position, player.graduation_year, player.province].filter(Boolean).join(" · ")}</div>
        </div>

        <label className="flex flex-col gap-1 mt-6">
          <span className="label-overline text-white/40 text-[10px]">Your name (parent / player)</span>
          <input data-testid="responder-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Smith" className="bg-white/5 border border-white/15 text-white rounded-sm px-3 py-2.5 text-sm placeholder:text-white/30" />
        </label>

        <div className="grid grid-cols-2 gap-3 mt-6">
          <button data-testid="accept-invitation-btn" disabled={submitting} onClick={() => respond("accept")} className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-5 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
            <Check className="w-4 h-4" /> Accept
          </button>
          <button data-testid="decline-invitation-btn" disabled={submitting} onClick={() => respond("decline")} className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 hover:border-red-400 hover:text-red-300 text-white px-5 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
            <X className="w-4 h-4" /> Decline
          </button>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-6">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}
